import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { DataGrid, gridClasses } from "@mui/x-data-grid";

const paginationFooterSx = {
  "& .MuiDataGrid-footerContainer": {
    borderTop: "1px solid #e2e8f0",
    minHeight: 52,
  },
  "& .MuiTablePagination-root": {
    overflow: "hidden",
  },
  "& .MuiTablePagination-toolbar": {
    minHeight: 52,
  },
  "& .MuiTablePagination-actions": {
    display: "flex",
    alignItems: "center",
  },
  "& .MuiTablePagination-actions .MuiIconButton-root": {
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 1.5,
    mx: 0.25,
    width: 32,
    height: 32,
  },
  "& .MuiTablePagination-actions .MuiIconButton-root.Mui-disabled": {
    opacity: 0.45,
  },
};

const noCellFocusOutlineSx = {
  [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
    outline: "none",
  },
  [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]:
    {
      outline: "none",
    },
};

const filterPanelSx = {
  "& .MuiDataGrid-panelWrapper": {
    maxWidth: "100%",
  },
  "& .MuiDataGrid-panelContent": {
    px: 2,
    py: 1.5,
  },
  "& .MuiDataGrid-filterForm": {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-end",
    gap: 1.25,
    p: 0.5,
  },
  "& .MuiDataGrid-filterFormDeleteIcon": {
    alignSelf: "center",
    mb: 0.25,
  },
  "& .MuiDataGrid-filterFormColumnInput": {
    minWidth: 150,
    flex: "1 1 150px",
  },
  "& .MuiDataGrid-filterFormOperatorInput": {
    minWidth: 130,
    flex: "1 1 130px",
  },
  "& .MuiDataGrid-filterFormValueInput": {
    minWidth: 170,
    flex: "1 1 170px",
  },
  "& .MuiDataGrid-filterForm .MuiFormControl-root": {
    m: 0,
  },
  "& .MuiDataGrid-filterFormLogicOperatorInput": {
    mb: 0.5,
  },
};

const filterPanelProps = {
  filterFormProps: {
    columnInputProps: {
      variant: "outlined",
      size: "small",
      label: "Columns",
    },
    operatorInputProps: {
      variant: "outlined",
      size: "small",
      label: "Operator",
    },
    valueInputProps: {
      variant: "outlined",
      size: "small",
      label: "Value",
      InputLabelProps: { shrink: true },
    },
  },
  sx: {
    "& .MuiDataGrid-filterForm": {
      alignItems: "flex-end",
    },
  },
};

/**
 * Shared data table wrapper.
 * Client pagination: leave paginationMode as "client" (default).
 * Server pagination: paginationMode="server" + rowCount + paginationModel + onPaginationModelChange.
 */
export default function DataTable({
  rows,
  columns,
  pageSize,
  showPagination = false,
  loading = false,
  columnVisibilityModel: columnVisibilityModelProp = {},
  onColumnVisibilityModelChange,
  filterModel: filterModelProp,
  onFilterModelChange,
  disableColumnFilter = false,
  disableColumnMenu = false,
  disableColumnSelector = false,
  disableColumnSorting = false,
  styling = {},
  autoHeight = false,
  getRowId,
  rowHeight = 52,
  density = "standard",
  paginationMode = "client",
  rowCount,
  paginationModel,
  onPaginationModelChange,
  pageSizeOptions = [10, 20, 30, 50, 100],
}) {
  const [tableHeight, setTableHeight] = useState(480);
  const [internalColumnVisibilityModel, setInternalColumnVisibilityModel] =
    useState(() => ({ ...columnVisibilityModelProp }));

  const isColumnVisibilityControlled =
    typeof onColumnVisibilityModelChange === "function";

  const columnVisibilityModel = isColumnVisibilityControlled
    ? columnVisibilityModelProp
    : internalColumnVisibilityModel;

  const handleColumnVisibilityModelChange = isColumnVisibilityControlled
    ? onColumnVisibilityModelChange
    : setInternalColumnVisibilityModel;

  const [internalFilterModel, setInternalFilterModel] = useState({
    items: [],
  });

  const isFilterControlled = typeof onFilterModelChange === "function";

  const filterModel = isFilterControlled
    ? filterModelProp ?? { items: [] }
    : internalFilterModel;

  const handleFilterModelChange = isFilterControlled
    ? onFilterModelChange
    : setInternalFilterModel;

  useEffect(() => {
    if (autoHeight) return undefined;

    const updateTableHeight = () => {
      const reserved = 280;
      const availableHeight = window.innerHeight - reserved;
      setTableHeight(availableHeight > 360 ? availableHeight : 360);
    };

    updateTableHeight();
    window.addEventListener("resize", updateTableHeight);
    return () => window.removeEventListener("resize", updateTableHeight);
  }, [autoHeight]);

  const isServer = paginationMode === "server";

  return (
    <Box
      sx={{
        width: "100%",
        ...(autoHeight
          ? {}
          : {
              height: tableHeight,
            }),
      }}
    >
      <DataGrid
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelChange}
        filterMode="client"
        disableColumnFilter={disableColumnFilter}
        disableColumnMenu={disableColumnMenu}
        disableColumnSelector={disableColumnSelector}
        disableColumnSorting={disableColumnSorting}
        componentsProps={{
          filterPanel: filterPanelProps,
        }}
        slotProps={{
          filterPanel: filterPanelProps,
        }}
        rows={rows}
        columns={columns}
        loading={loading}
        getRowId={getRowId}
        density={density}
        rowHeight={rowHeight}
        getRowHeight={() => rowHeight}
        disableRowSelectionOnClick
        autoHeight={autoHeight}
        paginationMode={paginationMode}
        rowCount={isServer ? rowCount : undefined}
        paginationModel={
          isServer
            ? paginationModel
            : undefined
        }
        onPaginationModelChange={
          isServer ? onPaginationModelChange : undefined
        }
        initialState={
          isServer
            ? undefined
            : {
                pagination: {
                  paginationModel: { pageSize: pageSize || 20, page: 0 },
                },
              }
        }
        pageSizeOptions={pageSizeOptions}
        hideFooter={!showPagination}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          backgroundColor: "background.paper",
          width: "100%",
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#f8fafc",
            borderBottom: "1px solid #e2e8f0",
            color: "#0f172a",
            fontWeight: 700,
          },
          "& .MuiDataGrid-cell": {
            borderBottomColor: "#eef2f7",
            display: "flex",
            alignItems: "center",
            overflow: "hidden",
            py: 0,
          },
          "& .MuiDataGrid-cellContent": {
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          },
          "& .MuiDataGrid-row": {
            maxHeight: `${rowHeight}px !important`,
            minHeight: `${rowHeight}px !important`,
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "#f8fbff",
          },
          ...noCellFocusOutlineSx,
          ...filterPanelSx,
          ...paginationFooterSx,
          ...styling,
        }}
      />
    </Box>
  );
}
